// Действията под прозореца „История на операциите": сторниране на цялата
// сметка, възстановяване, фактуриране и дубликат на бон.
//
// Изнесени от `BillOperationsModal.tsx` при редизайна — прозорецът показва,
// хукът решава кой бутон е активен и какво се случва при натискане. Въпросите
// преди „Сторнирай всичко" и „Възстанови" изреждат какво точно ще стане с
// тази сметка (`billActionConsequences.ts`) — голото „Сигурни ли сте?" не
// казваше какво излиза от касата и как се връщат парите.

import { useState } from "react";
import { App } from "antd";
import { ExclamationCircleOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";

import {
  useCancelAllBillItems,
  usePrintReceiptCopy,
  useRestoreBill,
} from "../../../../hooks/useBills";
import { useDefaultCompanyInvoiceIssuing } from "../../../../hooks/useCompanies";
import { usePrinterNotifications } from "../../../../hooks/usePrinterNotifications";
import { useAppSelector } from "../../../../store/hooks";
import {
  isRestoreDuplicateNameConflict,
  isUnknownFiscalOutcome,
  type CloseBillResult,
} from "../../../../api/bills";
import { billHistoryTableLabel, type BillHistory } from "../../../../api/reportsBillsHistory";
import { ConfirmConsequences } from "../../../../components/ConfirmConsequences";
import { isCashNoReceiptBill } from "../../../../lib/billFiscalization";
import { creditNoteOutcomeOf, softwareInvoicesAllowedIn } from "../../../../lib/fiscalInvoice";
import { getApiErrorMessage } from "../../../../utils/translations";
import { blockIfLicenseReadOnly } from "../../../../lib/licenseReadOnly";
import type { OpSummary } from "../../../../api/billOperations";
import type { Invoice } from "../../../../api/invoices";
import { cancelAllConsequences, restoreConsequences } from "./billActionConsequences";
import { cancelAllStockFailureReason, cancelAllStockFailureTexts } from "./cancelAllStockFailure";
import { hasFiscalReceiptToCopy } from "./receiptCopyGate";
import { useBillFiscalInvoice } from "../../../../hooks/useBillFiscalInvoice";

/** Прозорецът за фактуриране — синхронен с `InvoiceBillPickerModal`. */
const INVOICE_WINDOW_DAYS = 5;

/** Името на правото е същото като в настройките на ролите (seed_permissions). */
const RESTORE_NO_PERMISSION_HINT = "Нямате право „Възстановяване на сметки“.";

/** Въпросите изреждат последствията — по-широки от 416px на antd. */
const CONFIRM_WIDTH = 480;

type Params = {
  bill: BillHistory | null;
  /** Липсва, докато операциите се зареждат — тогава сторното е недостъпно. */
  summary: OpSummary | undefined;
  onClose: () => void;
  onBillUpdated?: () => void;
};

export const useBillOperationsActions = ({
  bill,
  summary,
  onClose,
  onBillUpdated,
}: Params) => {
  const { t } = useTranslation();
  const { modal, message } = App.useApp();
  const { notifyPrinterError, notifyPrinterTimeout } = usePrinterNotifications();
  const permissions = useAppSelector((state) => state.auth.permissions);
  const hasRestorePermission = permissions.some(
    (permission) => permission.name === "restore_last_bill",
  );

  const cancelAllMutation = useCancelAllBillItems();
  const restoreAllMutation = useRestoreBill();
  const receiptCopyMutation = usePrintReceiptCopy();

  const [billSelectOpen, setBillSelectOpen] = useState(false);
  const [selectedBills, setSelectedBills] = useState<BillHistory[]>([]);
  const [invoiceCreateOpen, setInvoiceCreateOpen] = useState(false);
  const [previewInvoice, setPreviewInvoice] = useState<Invoice | null>(null);
  // Сметка с фискална фактура: сторното ѝ е кредитно известие от апарата,
  // само за цялата сметка — бутонът и въпросът го казват предварително.
  const fiscalInvoice = useBillFiscalInvoice(bill);

  // Успехът на сторно: с кредитно известие — номерата му; известието е на
  // хартия, но не е записано във „Фактури" (`credit_note_error`) — текстът на
  // BE, кехлибарено и постоянно (не се издава наново); иначе „Готово".
  const notifyStornoSuccess = (result: CloseBillResult | undefined) => {
    const creditNote = creditNoteOutcomeOf(result, t);
    if (creditNote?.kind === "recordFailed") notifyPrinterTimeout(creditNote.text);
    else message.success(creditNote?.text ?? "Готово");
  };

  const handleCancelAll = () => {
    if (blockIfLicenseReadOnly()) return;
    if (!bill) return;
    modal.confirm({
      title: fiscalInvoice
        ? t("fiscalInvoice.storno.confirmTitle", { number: fiscalInvoice.invoice_number })
        : t("billActionConfirm.stornoTitle", { name: billHistoryTableLabel(bill) }),
      icon: <ExclamationCircleOutlined className="!text-brand-ink" />,
      // Какво точно излиза от касата и кой връща парите — преди въпроса.
      content: (
        <ConfirmConsequences
          lines={cancelAllConsequences(t, bill, fiscalInvoice)}
          question={t("billActionConfirm.stornoQuestion")}
        />
      ),
      okText: fiscalInvoice ? t("fiscalInvoice.storno.action") : t("billActionConfirm.stornoOk"),
      cancelText: "Отказ",
      centered: true,
      width: CONFIRM_WIDTH,
      okButtonProps: { style: { background: "#BD8A2C", borderColor: "#BD8A2C" } },
      onOk: async () => {
        try {
          const result = await cancelAllMutation.mutateAsync({
            billId: bill.id,
            data: { reason: "Сторниране на цяла сметка", isDefective: false },
          });
          onBillUpdated?.();
          onClose();
          const fiscal = result?.fiscal_result;
          if (!fiscal || fiscal.status === "success") notifyStornoSuccess(result);
          else if (isUnknownFiscalOutcome(fiscal.status))
            notifyPrinterTimeout(fiscal.error || "Принтерът не отговори навреме");
          else notifyPrinterError(fiscal.error || "Принтерът върна грешка");
        } catch (err) {
          const response = (err as { response?: { status?: number } })?.response;
          // 502 = принтерът отказа сторно бона и BE върна всичко назад —
          // нищо не е сторнирано; грешката стои до ръчно затваряне.
          if (response?.status === 502) {
            notifyPrinterError(getApiErrorMessage(err, "Принтерът върна грешка"));
            return;
          }
          // 409 `cancel_all_stock_failed` = складът не върна стоката на сметка
          // от разнесен отчет — нищо не е сторнирано и отпечатано; прозорецът
          // стои до ръчно затваряне (`cancelAllStockFailure.ts`)
          const stockFailure = cancelAllStockFailureReason(err);
          if (stockFailure != null) {
            const texts = cancelAllStockFailureTexts(t, stockFailure);
            modal.error({ title: texts.title, content: texts.text, centered: true });
            return;
          }
          // Останалото — текстът на BE на езика на екрана: и отказът на
          // кредитното известие (409 `{bg, en}` — вече издадено, апаратът не
          // може…), който е ПРЕДИ сторното — нищо не е сторнирано.
          message.error(getApiErrorMessage(err, "Грешка при сторниране"));
        }
      },
    });
  };

  const runRestore = async (allowDuplicateName: boolean) => {
    if (!bill) return;
    try {
      const result = await restoreAllMutation.mutateAsync({
        billId: bill.id,
        data: {
          reason: "Възстановяване на сметка",
          isDefective: false,
          allowDuplicateName,
        },
      });
      onBillUpdated?.();
      onClose();
      // Възстановяването издава сторно бон за старото приключване — изходът
      // от принтера се показва като при „Сторнирай всичко", вместо да се
      // губи зад общото „Готово".
      const fiscal = result?.fiscal_result;
      if (!fiscal || fiscal.status === "success") notifyStornoSuccess(result);
      else if (isUnknownFiscalOutcome(fiscal.status))
        notifyPrinterTimeout(fiscal.error || "Принтерът не отговори навреме");
      else notifyPrinterError(fiscal.error || "Принтерът върна грешка");
    } catch (err) {
      const response = (err as { response?: { status?: number } })?.response;
      // 409 с текст = вече има отворена маса със същото име. Питаме втори път,
      // вместо да отказваме — операторът решава дали да я дублира. 409 с
      // `{bg, en}` (отказът на кредитното известие) не се пита — показва се долу.
      if (!allowDuplicateName && isRestoreDuplicateNameConflict(err)) {
        confirmDuplicateName();
        return;
      }
      // 502 = принтерът отказа сторно бона — BE не е възстановил нищо.
      if (response?.status === 502) {
        notifyPrinterError(getApiErrorMessage(err, "Принтерът върна грешка"));
        return;
      }
      message.error(getApiErrorMessage(err, "Грешка при възстановяване"));
    }
  };

  const confirmDuplicateName = () => {
    if (!bill) return;
    modal.confirm({
      title: `Вече има отворена маса „${bill.name}“`,
      icon: <ExclamationCircleOutlined className="!text-brand-ink" />,
      content: `Ако продължите, ще има две отворени маси с име „${bill.name}“.`,
      okText: "Възстанови",
      cancelText: "Отказ",
      centered: true,
      okButtonProps: { style: { background: "#BD8A2C", borderColor: "#BD8A2C" } },
      onOk: () => runRestore(true),
    });
  };

  const handleRestoreAll = () => {
    if (blockIfLicenseReadOnly()) return;
    // Без правото бутонът е „заключен“, не disabled — натискането (на таблета
    // няма hover) казва защо, вместо да стигне до 403 от BE.
    if (!hasRestorePermission) {
      message.warning(RESTORE_NO_PERMISSION_HINT);
      return;
    }
    if (!bill) return;
    modal.confirm({
      title: t("billActionConfirm.restoreTitle", { name: billHistoryTableLabel(bill) }),
      icon: <ExclamationCircleOutlined className="!text-brand-ink" />,
      content: (
        <ConfirmConsequences
          lines={restoreConsequences(t, bill, fiscalInvoice)}
          question={t("billActionConfirm.restoreQuestion")}
        />
      ),
      okText: t("billActionConfirm.restoreOk"),
      cancelText: "Отказ",
      centered: true,
      width: CONFIRM_WIDTH,
      okButtonProps: { style: { background: "#BD8A2C", borderColor: "#BD8A2C" } },
      onOk: () => runRestore(false),
    });
  };

  const daysSinceClosed = bill?.closed_at
    ? dayjs().startOf("day").diff(dayjs(bill.closed_at).startOf("day"), "day")
    : Infinity;
  // Закритата в брой без бон („Закриване на сметка(1)") няма фискален
  // документ, срещу който да се издаде фактура — бутонът стои сив.
  const isCashNoReceipt = !!bill && isCashNoReceiptBill(bill);
  const canInvoice =
    !!bill &&
    bill.status === "closed" &&
    !bill.is_invoiced &&
    !isCashNoReceipt &&
    daysSinceClosed <= INVOICE_WINDOW_DAYS;
  // Фирмата е във „Фискално устройство": софтуерна фактура няма (BE отказва
  // всяка) — „Фактуриране" е сив, но натискаем, и казва защо (като „Възстанови"
  // без правото). Фирмата се пита само когато бутонът изобщо би бил активен.
  const invoiceIssuing = useDefaultCompanyInvoiceIssuing({ enabled: canInvoice });
  const invoiceLocked = !softwareInvoicesAllowedIn(invoiceIssuing);
  const invoiceHint = invoiceLocked ? t("fiscalInvoice.mode.softwareOff") : undefined;

  const handleInvoice = () => {
    if (blockIfLicenseReadOnly()) return;
    if (!bill) return;
    if (invoiceHint) {
      message.warning(invoiceHint);
      return;
    }
    if (!canInvoice) {
      if (bill.is_invoiced) message.warning(t("invoicing.warnAlreadyInvoiced"));
      else if (bill.status !== "closed")
        message.warning(t("invoicing.warnOnlyClosed"));
      else if (isCashNoReceipt)
        message.warning(t("invoicing.warnCashNoReceipt"));
      else
        message.warning(
          t("invoicing.warnWindowExpired", { days: INVOICE_WINDOW_DAYS }),
        );
      return;
    }
    // Стъпка 1 — избор на сметка/и. Текущата е пред-избрана, но операторът
    // може да добави и други нефактурирани сметки от последните дни.
    setBillSelectOpen(true);
  };

  const activeAmount = summary
    ? Number(summary.gross_revenue) + Number(summary.cancelled_amount)
    : 0;
  // „Сторнирай всичко" — има активни (несторнирани) редове, които да се отменят.
  const canCancel = activeAmount > 0;
  // „Възстанови" — всяка затворена сметка с активни редове. Възстановяването
  // отваря СЪЩАТА сметка наново (същото id, `service_restore.py`): бонът ѝ се
  // сторнира, редовете остават, старото плащане отива в хронологията. Броят
  // сторнирани редове няма нищо общо с това дали е възможно — с предишното
  // условие `cancelled_count > 0` бутонът стоеше сив точно за сметките, които
  // най-често се възстановяват. Тук няма и времеви прозорец, за разлика от
  // списъка в „Маркиране" (последните 2 часа).
  const canRestore = bill?.status === "closed";
  // Правото `restore_last_bill` (BE го налага в POST /bills/{id}/restore).
  // В „Маркиране" бутонът се крие без него; тук стои сив с обяснение.
  const restoreLocked = !hasRestorePermission;
  const restoreHint = restoreLocked ? RESTORE_NO_PERMISSION_HINT : undefined;

  // „Дубликат на бон" — копие от КЛЕН на вече издадения фискален бон.
  // Четирите условия са огледало на BE-то (`router_receipt_copy.py`),
  // което ги налага наистина; тук са, за да е ясно ПРЕДИ натискане кои
  // редове изобщо имат бон. Фискализирана значи „има номер И ненулева
  // сума на бона" (поръчано + бакшиш) — затворена сметка с нулев тотал
  // (плащане „по сметка") минава без фискален документ и няма какво да се
  // копира (`receiptCopyGate.ts`).
  const isFiscalised = hasFiscalReceiptToCopy(bill);
  const isClosedToday =
    !!bill?.closed_at && dayjs(bill.closed_at).isSame(dayjs(), "day");
  const canPrintReceiptCopy =
    bill?.status === "closed" && isFiscalised && isClosedToday;
  // Сивият бутон без обяснение е неразличим от счупен — затова причината
  // стои в tooltip-а.
  const receiptCopyHint = canPrintReceiptCopy
    ? `Копие от КЛЕН на бон № ${bill?.receipt_number}`
    : bill?.status !== "closed"
      ? "Копие се печата само на затворена сметка."
      : !isFiscalised
        ? "Сметката няма издаден фискален бон."
        : "Копие може да се извади само за сметка, затворена днес.";

  const handlePrintReceiptCopy = async () => {
    if (blockIfLicenseReadOnly()) return;
    // Мутацията е и защитата от двойно натискане: бутонът е disabled,
    // докато заявката е в полет, а ранният изход хваща и второто кликане,
    // стигнало преди пре-рендера.
    if (!bill || receiptCopyMutation.isPending) return;
    try {
      await receiptCopyMutation.mutateAsync({ billId: bill.id });
      message.success("Копието е изпратено за печат");
    } catch (err) {
      const detail = (err as { response?: { data?: { detail?: string } } })
        ?.response?.data?.detail;
      notifyPrinterError(detail || "Грешка при печат на копието");
    }
  };

  return {
    canCancel,
    /** „Кредитно известие" при сметка с фискална фактура; иначе старият етикет. */
    cancelLabel: fiscalInvoice ? t("fiscalInvoice.storno.action") : undefined,
    canRestore: !!canRestore,
    restoreLocked,
    restoreHint,
    canInvoice,
    invoiceLocked,
    invoiceHint,
    canPrintReceiptCopy: !!canPrintReceiptCopy,
    receiptCopyHint,
    cancelLoading: cancelAllMutation.isPending,
    restoreLoading: restoreAllMutation.isPending,
    receiptCopyLoading: receiptCopyMutation.isPending,
    handleCancelAll,
    handleRestoreAll,
    handleInvoice,
    handlePrintReceiptCopy,
    invoiceFlow: {
      billSelectOpen,
      closeBillSelect: () => setBillSelectOpen(false),
      confirmBillSelect: (bills: BillHistory[]) => {
        setSelectedBills(bills);
        setBillSelectOpen(false);
        setInvoiceCreateOpen(true);
      },
      selectedBills,
      invoiceCreateOpen,
      closeInvoiceCreate: () => setInvoiceCreateOpen(false),
      onInvoiceSaved: (invoice: Invoice) => {
        setInvoiceCreateOpen(false);
        setPreviewInvoice(invoice);
      },
      previewInvoice,
      closePreview: () => setPreviewInvoice(null),
    },
  };
};
